import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  getAllCustomerContractsApi,
  getArchivedCustomerContractsApi,
  downloadContractPdfApi,
  sendContractAgreementEmail,
  deleteCustomerContractApi,
  archiveCustomerContractApi,
  unarchiveCustomerContractApi,
  CustomerContractListItem,
  CONTRACT_STATUS_DRAFT,
  CONTRACT_STATUS_ACTIVE,
  CONTRACT_STATUS_EXPIRED,
  CONTRACT_STATUS_CANCELLED,
  getPreferredContractLanguageApi,
} from '../features/customer-contract/customer-contract.service';
import { fetchPersonalData, fetchUserPreferences } from '../features/profile-settings/profile-settings.slice';
import { resolveContractLang, CONTRACT_LANG_STORAGE_KEY } from '../features/customer-contract/customer-contract-language.util';
import { formatContractDate } from '../features/customer-contract/customer-contract-date.util';
import { ContractLanguageDropdown } from './contract-language-dropdown.component';
import './customer-contracts-grid.component.scss';

const CONTRACT_STATUS: Record<number, { label: string; cls: string }> = {
  0: { label: 'CCG_STATUS_DRAFT',             cls: 'draft' },
  1: { label: 'CCG_STATUS_PENDING_SIGNATURE', cls: 'pending-signature' },
  2: { label: 'CCG_STATUS_ACTIVE',            cls: 'active' },
  3: { label: 'CCG_STATUS_EXPIRED',           cls: 'expired' },
  4: { label: 'CCG_STATUS_CANCELLED',         cls: 'cancelled' },
};

const PAGE_SIZE = 10;

const formatDate = formatContractDate;

function formatAmount(amount: number, currency: string): string {
  return `${currency} ${amount.toLocaleString()}`;
}

// Client avatar helpers — mirror the platform data table (My Invoices).
function getClientInitial(name?: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
}

function getAvatarVariant(name?: string): number {
  const key = (name || '').trim();
  let sum = 0;
  for (let i = 0; i < key.length; i++) sum += key.charCodeAt(i);
  return (sum % 5) + 1;
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="3" y="4.5" width="18" height="17" rx="2.5" stroke="currentColor" strokeWidth="2" />
      <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1v9M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.5 2.5a1.414 1.414 0 012 2L5 13H3v-2L11.5 2.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.5 1.5L7.5 8.5M14.5 1.5L10 14.5L7.5 8.5M14.5 1.5L1.5 6L7.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function DeleteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 4h12M5.5 4V2.5a1 1 0 011-1h3a1 1 0 011 1V4M6.5 7.5v4M9.5 7.5v4M3.5 4l.6 9a1 1 0 001 .9h5.8a1 1 0 001-.9l.6-9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function DuplicateIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.5 5.5V3a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1h-2.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M3.5 5.5h6a1 1 0 011 1v6a1 1 0 01-1 1h-6a1 1 0 01-1-1v-6a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 3a1 1 0 011-1h10a1 1 0 011 1v2H2V3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M2.5 5.5V12a1 1 0 001 1h9a1 1 0 001-1V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M6 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function UnarchiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 3a1 1 0 011-1h10a1 1 0 011 1v2H2V3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M2.5 5.5V12a1 1 0 001 1h9a1 1 0 001-1V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M6 9.5L8 7.5L10 9.5M8 7.5V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export const CustomerContractsGrid = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const userId = useSelector((state: any) => state.auth?.user?.userId);
  const personalData = useSelector((state: any) => state.profileSettings?.personalData);
  const userPreferences = useSelector((state: any) => state.profileSettings?.userPreferences);
  const [activeTab, setActiveTab] = useState<'all' | 'archived'>('all');
  const [contracts, setContracts] = useState<CustomerContractListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const language = new URLSearchParams(window.location.search).get('lang')

  // ── Sliding pill-tabs indicator ──────────────────────────────────────────────
  const tabsRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [tabsReady, setTabsReady] = useState(false);

  function measureIndicator() {
    const el = tabsRef.current?.querySelector<HTMLButtonElement>('.ccg-pill-tabs__tab.is-active');
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }

  // Position before paint so there's no flash / slide-in on first mount.
  useLayoutEffect(() => {
    measureIndicator();
  }, [activeTab]);

  useEffect(() => {
    window.addEventListener('resize', measureIndicator);
    return () => window.removeEventListener('resize', measureIndicator);
  }, []);

  // Enable the slide transition only after the first frame, so the pill animates
  // on real tab switches instead of sliding in from the left on mount.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setTabsReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // ── Action tooltip ───────────────────────────────────────────────────────────
  // Rendered through a portal as a fixed element, so it always floats on top and
  // never widens the table / triggers the horizontal scrollbar. Hover handlers
  // live on a wrapper span so the tooltip still shows for disabled buttons.
  const tipRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [tipShift, setTipShift] = useState(0);
  const showTip = (el: HTMLElement, text: string) => {
    const r = el.getBoundingClientRect();
    setTipShift(0);
    setTip({ text, x: r.left + r.width / 2, y: r.top });
  };
  const hideTip = () => setTip(null);
  const tipHandlers = (text: string) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => showTip(e.currentTarget, text),
    onMouseLeave: hideTip,
    onFocus: (e: React.FocusEvent<HTMLElement>) => showTip(e.currentTarget, text),
    onBlur: hideTip,
  });

  // Keep the tooltip inside the viewport: measure it and shift horizontally if it
  // would overflow either edge (the arrow shifts the opposite way to stay on the button).
  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const margin = 8;
    const rect = tipRef.current.getBoundingClientRect();
    let delta = 0;
    if (rect.left < margin) delta = margin - rect.left;
    else if (rect.right > window.innerWidth - margin) delta = window.innerWidth - margin - rect.right;
    if (delta !== 0) setTipShift((s) => s + delta);
  }, [tip]);

  function loadContracts(tab: 'all' | 'archived') {
    setLoading(true);
    setError(false);
    setPage(1);
    const apiCall = tab === 'archived' ? getArchivedCustomerContractsApi : getAllCustomerContractsApi;
    apiCall()
      .then(res => setContracts(res.response || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadContracts(activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (userId) dispatch(fetchPersonalData(userId));
    dispatch(fetchUserPreferences());
  }, [dispatch, userId]);

  const [lang, setLang] = useState(resolveContractLang);

  // Scoped to contract actions (PDF locale, sent-email locale) only — does
  // not touch the platform-wide UI language.
  const handleContractLangChange = (newLang: string) => {
    setLang(newLang);
    window.localStorage.setItem(CONTRACT_LANG_STORAGE_KEY, newLang);
  };

  // The backend remembers the freelancer's contract-language preference from
  // the last contract they created — apply it here so the dropdown reflects
  // that saved preference. An explicit stored contract language always wins.
  useEffect(() => {
    if (window.localStorage.getItem(CONTRACT_LANG_STORAGE_KEY)) return;

    getPreferredContractLanguageApi(lang)
      .then(res => {
        const preferred = res.response;
        if (preferred && preferred !== lang) handleContractLangChange(preferred);
      })
      .catch(() => { /* keep the locally-resolved default */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDownloadPdf(contract: CustomerContractListItem) {
    setDownloadingId(contract.id);
    try {
      const blob = await downloadContractPdfApi(contract.id, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract-${contract.contractNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('CCG_DOWNLOAD_SUCCESS') as string);
    } catch {
      toast.error(t('CCG_DOWNLOAD_ERROR') as string);
    } finally {
      setDownloadingId(null);
    }
  }

  function handleEdit(contract: CustomerContractListItem) {
    if (contract.isArchived) return;
    navigate(`/v2/create-contract?id=${contract.id}&lang=${language}`);
  }

  async function handleSendEmail(contract: CustomerContractListItem) {
    if (
      contract.isArchived ||
      contract.contractStatus === CONTRACT_STATUS_EXPIRED ||
      contract.contractStatus === CONTRACT_STATUS_ACTIVE ||
      contract.contractStatus === CONTRACT_STATUS_CANCELLED
    ) return;
    if (!contract.customer?.email) {
      toast.error(t('CCG_SEND_NO_EMAIL') as string);
      return;
    }

    setSendingId(contract.id);
    try {
      const senderName =
        personalData?.fullName?.trim() ||
        `${personalData?.firstName ?? ''} ${personalData?.lastName ?? ''}`.trim() ||
        userPreferences?.organizationName ||
        '';

      await sendContractAgreementEmail(
        contract.id,
        {
          customerName: contract.customer.name,
          customerEmail: contract.customer.email,
          contractNumber: contract.contractNumber,
          startDate: contract.startDate,
          endDate: contract.endDate,
          noEndDate: contract.noEndDate,
          amount: contract.amount,
          currency: contract.currency,
          compensationType: contract.compensationType,
        },
        senderName,
        lang,
        contract.contractStatus
      );
      loadContracts('all')

      toast.success(t('CCG_SEND_SUCCESS') as string);
    } catch {
      toast.error(t('CCG_SEND_ERROR') as string);
    } finally {
      setSendingId(null);
    }
  }

  function handleDuplicate(contract: CustomerContractListItem) {
    navigate(`/v2/create-contract?duplicateId=${contract.id}&lang=${language}`);
  }

  async function handleToggleArchive(contract: CustomerContractListItem) {
    const willArchive = !contract.isArchived;
    setArchivingId(contract.id);
    try {
      if (willArchive) {
        await archiveCustomerContractApi(contract.id);
      } else {
        await unarchiveCustomerContractApi(contract.id);
      }
      loadContracts(activeTab);
      toast.success(t(willArchive ? 'CCG_ARCHIVE_SUCCESS' : 'CCG_UNARCHIVE_SUCCESS') as string);
    } catch {
      toast.error(t(willArchive ? 'CCG_ARCHIVE_ERROR' : 'CCG_UNARCHIVE_ERROR') as string);
    } finally {
      setArchivingId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(contracts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = contracts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="ccg">
      <div className="ccg__header">
        <h2 className="ccg__title">{t('CCG_TITLE')}</h2>
        <ContractLanguageDropdown value={lang} onChange={handleContractLangChange} />
      </div>

      <div className="ccg__tabs">
        <div className="ccg-pill-tabs" role="tablist" ref={tabsRef} aria-label={t('CCG_TITLE') as string}>
          <span
            className={`ccg-pill-tabs__indicator${tabsReady ? ' is-animated' : ''}`}
            aria-hidden="true"
            style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
          />
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'all'}
            className={`ccg-pill-tabs__tab${activeTab === 'all' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            {t('CCG_TAB_ALL')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'archived'}
            className={`ccg-pill-tabs__tab${activeTab === 'archived' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('archived')}
          >
            {t('CCG_TAB_ARCHIVED')}
          </button>
        </div>
      </div>

      {loading && (
        <div className="ccg__skeleton" aria-hidden="true">
          <div className="ccg__skel-head" />
          {Array.from({ length: 7 }).map((_, i) => (
            <div className="ccg__skel-row" key={i}>
              {Array.from({ length: 8 }).map((__, j) => (
                <div className="ccg__skel-cell" key={j} />
              ))}
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="ccg__state ccg__state--error">
          <p className="ccg__state-text">{t('CCG_ERROR')}</p>
        </div>
      )}

      {!loading && !error && contracts.length === 0 && (
        <div className="ccg__state ccg__state--empty">
          <span className="ccg__empty-icon" aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-6l-2 3h-4l-2-3H2" />
              <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
          </span>
          <p className="ccg__state-text">{t(activeTab === 'archived' ? 'CCG_EMPTY_ARCHIVED' : 'CCG_EMPTY')}</p>
          {activeTab === 'all' && (
            <button
              className="ccg__create-btn"
              onClick={() => navigate(`/v2/create-contract?lang=${lang}`)}
            >
              {t('CCG_CREATE_FIRST')}
            </button>
          )}
        </div>
      )}

      {!loading && !error && contracts.length > 0 && (
        <>
        <div className="ccg__table-wrap">
          <table className="ccg__table">
            <thead>
              <tr className="ccg__thead-row">
                <th className="ccg__th">{t('CCG_COL_CONTRACT_NO')}</th>
                <th className="ccg__th">{t('CCG_COL_CUSTOMER')}</th>
                <th className="ccg__th">{t('CCG_COL_START_DATE')}</th>
                <th className="ccg__th">{t('CCG_COL_END_DATE')}</th>
                <th className="ccg__th">{t('CCG_COL_AMOUNT')}</th>
                <th className="ccg__th">{t('CCG_COL_STATUS')}</th>
                <th className="ccg__th">{t('CCG_COL_CREATED')}</th>
                <th className="ccg__th">{t('CCG_COL_ACTIONS')}</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(contract => {
                const status = CONTRACT_STATUS[contract.contractStatus] ?? {
                  label: 'CCG_STATUS_DRAFT',
                  cls: 'draft',
                };
                const isDownloading = downloadingId === contract.id;
                const editTip = contract.isArchived
                  ? t('CCG_ACTION_EDIT_DISABLED_ARCHIVED')
                  : contract.contractStatus === CONTRACT_STATUS_DRAFT
                    ? t('CCG_ACTION_EDIT')
                    : t('CCG_ACTION_EDIT_DISABLED');
                const sendTip = contract.isArchived
                  ? t('CCG_ACTION_SEND_DISABLED_ARCHIVED')
                  : contract.contractStatus === CONTRACT_STATUS_EXPIRED
                    ? t('CCG_ACTION_SEND_DISABLED_EXPIRED')
                    : contract.contractStatus === CONTRACT_STATUS_ACTIVE
                      ? t('CCG_ACTION_SEND_DISABLED_ACTIVE')
                      : contract.contractStatus === CONTRACT_STATUS_CANCELLED
                        ? t('CCG_ACTION_SEND_DISABLED_CANCELLED')
                        : t('CCG_ACTION_SEND');
                const archiveTip = t(contract.isArchived ? 'CCG_ACTION_UNARCHIVE' : 'CCG_ACTION_ARCHIVE');
                const downloadTip = t('CCG_ACTION_DOWNLOAD_PDF');
                const duplicateTip = t('CCG_ACTION_DUPLICATE');
                return (
                  <tr key={contract.id} className="ccg__tr">
                    <td className="ccg__td ccg__td--contract-no">
                      {contract.contractNumber}
                    </td>
                    <td className="ccg__td">
                      <span className="ccg__client">
                        <span className={`ccg__avatar ccg__avatar--v${getAvatarVariant(contract.customer?.name)}`}>
                          {getClientInitial(contract.customer?.name)}
                        </span>
                        <span className="ccg__client-info">
                          <span className="ccg__customer-name">{contract.customer?.name ?? '—'}</span>
                          <span className="ccg__customer-email">{contract.customer?.email ?? ''}</span>
                        </span>
                      </span>
                    </td>
                    <td className="ccg__td">
                      <span className="ccg__date"><CalendarIcon /> {formatDate(contract.startDate)}</span>
                    </td>
                    <td className="ccg__td">
                      {contract.noEndDate ? (
                        <span className="ccg__no-end">{t('CCG_NO_END_DATE')}</span>
                      ) : (
                        <span className="ccg__date"><CalendarIcon /> {formatDate(contract.endDate)}</span>
                      )}
                    </td>
                    <td className="ccg__td ccg__td--amount">
                      <span className="ccg__amount-pill">{formatAmount(contract.amount, contract.currency)}</span>
                    </td>
                    <td className="ccg__td">
                      <span className={`ccg__badge ccg__badge--${status.cls}`}>
                        {t(status.label)}
                      </span>
                    </td>
                    <td className="ccg__td ccg__td--muted">{formatDate(contract.createdDate)}</td>
                    <td className="ccg__td">
                      <div className="ccg__actions">
                        <span className="ccg__action-wrap" {...tipHandlers(downloadTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--download"
                            onClick={() => handleDownloadPdf(contract)}
                            disabled={isDownloading}
                            aria-label={downloadTip as string}
                          >
                            {isDownloading ? (
                              <span className="ccg__action-spinner" />
                            ) : (
                              <DownloadIcon />
                            )}
                          </button>
                        </span>
                        <span className="ccg__action-wrap" {...tipHandlers(editTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--edit"
                            onClick={() => handleEdit(contract)}
                            disabled={contract.isArchived || contract.contractStatus !== CONTRACT_STATUS_DRAFT}
                            aria-label={editTip as string}
                          >
                            <EditIcon />
                          </button>
                        </span>
                        <span className="ccg__action-wrap" {...tipHandlers(sendTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--send"
                            onClick={() => handleSendEmail(contract)}
                            disabled={
                              contract.isArchived ||
                              contract.contractStatus === CONTRACT_STATUS_EXPIRED ||
                              contract.contractStatus === CONTRACT_STATUS_ACTIVE ||
                              contract.contractStatus === CONTRACT_STATUS_CANCELLED ||
                              sendingId === contract.id
                            }
                            aria-label={sendTip as string}
                          >
                            {sendingId === contract.id ? (
                              <span className="ccg__action-spinner" />
                            ) : (
                              <SendIcon />
                            )}
                          </button>
                        </span>
                        {contract.contractStatus === CONTRACT_STATUS_EXPIRED && (
                          <span className="ccg__action-wrap" {...tipHandlers(duplicateTip as string)}>
                            <button
                              className="ccg__action-btn ccg__action-btn--duplicate"
                              onClick={() => handleDuplicate(contract)}
                              aria-label={duplicateTip as string}
                            >
                              <DuplicateIcon />
                            </button>
                          </span>
                        )}
                        <span className="ccg__action-wrap" {...tipHandlers(archiveTip as string)}>
                          <button
                            className="ccg__action-btn ccg__action-btn--archive"
                            onClick={() => handleToggleArchive(contract)}
                            disabled={archivingId === contract.id}
                            aria-label={archiveTip as string}
                          >
                            {archivingId === contract.id ? (
                              <span className="ccg__action-spinner" />
                            ) : contract.isArchived ? (
                              <UnarchiveIcon />
                            ) : (
                              <ArchiveIcon />
                            )}
                          </button>
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="ccg__pagination">
            <div className="ccg__pager">
              <button
                className="ccg__page-btn ccg__page-btn--nav"
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label={t('PREV', { defaultValue: 'Previous' }) as string}
              >
                <ChevronLeftIcon />
                {t('PREV', { defaultValue: 'Prev' })}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`ccg__page-btn ccg__page-btn--num${p === currentPage ? ' ccg__page-btn--active' : ''}`}
                  onClick={() => setPage(p)}
                  aria-current={p === currentPage ? 'page' : undefined}
                >
                  {p}
                </button>
              ))}
              <button
                className="ccg__page-btn ccg__page-btn--nav"
                disabled={currentPage === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label={t('NEXT', { defaultValue: 'Next' }) as string}
              >
                {t('NEXT', { defaultValue: 'Next' })}
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
        </>
      )}

      {tip && createPortal(
        <div
          ref={tipRef}
          className="ccg-tip"
          style={{
            left: tip.x,
            top: tip.y,
            transform: `translate(calc(-50% + ${tipShift}px), calc(-100% - 8px))`,
            ['--tip-arrow' as string]: `${-tipShift}px`,
          } as React.CSSProperties}
        >
          {tip.text}
        </div>,
        document.body,
      )}
    </div>
  );
};
